import { useState, FormEvent } from 'react';
import { Shield, Eye, EyeOff, LogIn } from 'lucide-react';
import { Loader } from './ui/Loader';
import { toast } from '../utils/toast';
import { API_BASE } from '../utils/supabase/info';
import { devError } from '../utils/logger';


interface SubAdminLoginProps {
  onLoginSuccess: (session: SubAdminSession) => void;
  onSwitchToMainAdmin?: () => void;
  onBackToNormal?: () => void;
}

export interface SubAdminSession {
  adminId: string;
  username: string;
  displayName: string | null;
  permissions: string[];
  expiresAt: string;
}

export function SubAdminLogin({ onLoginSuccess, onSwitchToMainAdmin, onBackToNormal }: SubAdminLoginProps) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e: FormEvent) => {
    e.preventDefault();
    
    if (!username || !password) {
      toast.error('Vennligst fyll inn brukernavn og passord');
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(
        `${API_BASE}/admin/sub-admins/login`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ username, password })
        }
      );

      const data = await response.json();

      if (response.ok && data.success) {
        const session: SubAdminSession = {
          adminId: data.session.admin_id,
          username: data.session.username,
          displayName: data.session.display_name,
          permissions: data.session.permissions,
          expiresAt: data.session.expires_at
        };

        // Store session in localStorage
        localStorage.setItem('sub_admin_session', JSON.stringify(session));

        toast.success(`Velkommen, ${session.displayName || session.username}!`);
        onLoginSuccess(session);
      } else {
        toast.error(data.error || 'Ugyldig brukernavn eller passord');
      }
    } catch (error) {
      devError('Login error:', error);
      toast.error('Kunne ikke logge inn');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-orange-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl shadow-2xl p-8 w-full max-w-md">
        {/* Logo */}
        <div className="flex justify-center mb-6">
          <div className="w-16 h-16 bg-gradient-to-br from-purple-600 to-pink-600 rounded-2xl flex items-center justify-center">
            <Shield className="w-8 h-8 text-white" />
          </div>
        </div>

        {/* Title */}
        <h1 className="text-3xl text-center mb-2">
          <span className="bg-gradient-to-r from-purple-600 via-pink-600 to-orange-600 bg-clip-text text-transparent pb-1">
            Talentfunn
          </span>
        </h1>
        <p className="text-center text-gray-600 mb-8">Admin-portal</p>

        {/* Login Form */}
        <form onSubmit={handleLogin} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Brukernavn
            </label>
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="ola_nordmann"
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
              disabled={loading}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Passord
            </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all pr-12"
                disabled={loading}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                disabled={loading}
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl hover:shadow-lg transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed mt-6"
          >
            {loading ? (
              <Loader size="md" className="text-white" />
            ) : (
              <>
                <LogIn className="w-5 h-5" />
                Logg inn
              </>
            )}
          </button>
        </form>

        {/* Footer */}
        <div className="mt-6 pt-6 border-t border-gray-200 space-y-3">
          {onSwitchToMainAdmin && (
            <button
              onClick={onSwitchToMainAdmin}
              className="w-full text-purple-600 hover:text-purple-800 transition-colors text-sm"
            >
              Logg inn som hovedadmin
            </button>
          )}
          {onBackToNormal && (
            <button
              onClick={onBackToNormal}
              className="w-full text-gray-600 hover:text-gray-900 transition-colors text-sm"
            >
              Tilbake til vanlig innlogging
            </button>
          )}
          <p className="text-center text-xs text-gray-500">
            Kontakt hovedadministrator hvis du har problemer med innlogging
          </p>
        </div>
      </div>
    </div>
  );
}
